/**
 * Shared core logic for running battery design assistant
 * Used by both chat actions (handleRunBatteryDesignAssistant) and voice tools (RunBatteryDesignAssistantToolCall)
 */

export interface BatteryDesignAssistantArgs {
  customer_goal?: 'maximize_savings' | 'self_consumption' | 'peak_demand_shaving'
  backup_hours?: number
  continuous_power_kw?: number
  peak_power_kw?: number
  codes?: string[]
  num_recommendations?: number
  years_to_simulate?: number
}

export interface BatteryDesignAssistantResult {
  batterySets: any[]
  description: string
  customerPriority?: string
  essentialBackupMetrics?: {
    backup_hours?: number
    continuous_power_kw?: number
    peak_power_kw?: number
  }
}

const getUnsavedData = (systemUuid: string) => {
  try {
    const restPostData = window.projectForm ? window.projectForm.mutators.getUnsavedChangeAffectSystemCalcs() : {}
    const sceneJSONFiltered = window.editor?.sceneSystemsOnlyAsJSON([systemUuid], true)

    var system_current_matches = window.editor?.filterObjects(function (s) {
      return s.type === 'OsSystem' && s.userData.is_current === true
    })
    if (system_current_matches.length > 0) {
      for (var i = 0; i < sceneJSONFiltered['object']['children'].length; i++) {
        var s = sceneJSONFiltered['object']['children'][i]
        if (s.userData.is_current !== true) {
          s.userData.system_current = system_current_matches[0].userData
        }
      }
    }
    return {
      ...restPostData,
      design: sceneJSONFiltered && window.CompressionHelper.compress(JSON.stringify(sceneJSONFiltered)),
    }
  } catch (e) {
    console.error('runBatteryDesignAssistantCore: Failed to get unsaved data', e)
    return {}
  }
}

/**
 * Core logic for running battery design assistant
 * @param args - Battery design assistant arguments
 * @param systemUuid - UUID of the selected system
 * @param projectId - Project ID
 * @param apiCall - Function to make the API call, should return the battery sets array
 * @returns Battery design assistant result with batterySets and description
 */
export async function runBatteryDesignAssistantCore(
  args: BatteryDesignAssistantArgs,
  systemUuid: string,
  projectId: number,
  apiCall: (postData: any) => Promise<any[]>
): Promise<BatteryDesignAssistantResult> {
  // Get the selected system
  const system = window.editor?.selectedSystem
  if (!system || system.uuid !== systemUuid) {
    throw new Error('No system selected. Please select a system in the design.')
  }

  // Prepare request parameters
  const unsavedDesignData =
    (window.projectForm?.mutators.getFormDirtyFields().length > 0 && getUnsavedData(system.uuid)) || {}

  const postData = {
    customer_goal: args.customer_goal || 'self_consumption',
    backup_hours: args.backup_hours,
    continuous_power_kw: args.continuous_power_kw,
    peak_power_kw: args.peak_power_kw,
    codes: args.codes,
    num_recommendations: args.num_recommendations || 3,
    years_to_simulate: args.years_to_simulate || 1,
    ...unsavedDesignData,
    include_unsaved_project: true,
  }

  // Call the API to get battery recommendations
  const batterySets = await apiCall(postData)

  if (!Array.isArray(batterySets) || batterySets.length === 0) {
    throw new Error('No battery recommendations found for the specified criteria.')
  }

  // Build description
  const description = `Found ${batterySets.length} recommendation${batterySets.length > 1 ? 's' : ''}`

  return {
    batterySets,
    description,
    customerPriority: args.customer_goal,
    essentialBackupMetrics: {
      backup_hours: args.backup_hours,
      continuous_power_kw: args.continuous_power_kw,
      peak_power_kw: args.peak_power_kw,
    },
  }
}

